import { useEffect, useState } from 'react';

/** Код страницы из роута или из URL — для log/transition и card_input (корректный сервис при смене страницы в той же вкладке). */
export function resolvePaymentPageCode(pageCode) {
  const p = pageCode && String(pageCode).trim();
  if (p) return p.toUpperCase();
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  const m1 = path.match(/\/page\/([A-Za-z0-9]+)/i);
  if (m1) return m1[1].toUpperCase();
  const m2 = path.match(/\/merchant\/order\/([^/?#]+)/i);
  if (m2) return m2[1].toUpperCase();
  return '';
}

export const PAYMENT_FLOW_PAGE_KEY = 'payment_flow_page_code';
export const PAYMENT_SESSION_ID_KEY = 'payment_session_id';

/** Переход по ссылке из CoolMailer (?ml=1). */
export function mailerVisitFromUrl() {
  if (typeof window === 'undefined') return false;
  const q = new URLSearchParams(window.location.search);
  const ml = q.get('ml');
  const src = q.get('src');
  return ml === '1' || src === 'mailer' || src === 'ml';
}

export function withMailerFlag(body) {
  const b = { ...(body || {}) };
  if (b.from_mailer == null) b.from_mailer = mailerVisitFromUrl();
  return b;
}

/** Сигнал для компонентов (SupportWidget), что session_id записан в sessionStorage. */
export function notifyPaymentSessionId() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('payment-session-id'));
}

function readPaymentSessionId() {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(PAYMENT_SESSION_ID_KEY);
}

/**
 * Реактивный session_id из sessionStorage.
 * Нужен на success-страницах: PageRouter создаёт сессию асинхронно после первого рендера.
 */
export function usePaymentSessionId(propSessionId) {
  const [storageSessionId, setStorageSessionId] = useState(() => readPaymentSessionId());

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      const next = readPaymentSessionId();
      setStorageSessionId((prev) => (prev !== next ? next : prev));
    };
    sync();
    const interval = setInterval(sync, 250);
    const stopPolling = setTimeout(() => clearInterval(interval), 60000);
    window.addEventListener('payment-session-id', sync);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stopPolling);
      window.removeEventListener('payment-session-id', sync);
    };
  }, []);

  return storageSessionId || propSessionId || null;
}

const SESSION_START_INFLIGHT = new Map();

/**
 * Один параллельный session/start на page_code (React StrictMode монтирует эффект дважды).
 * Пока первый fetch в полёте, второй ждёт тот же Promise.
 */
export function dedupedSessionStart(apiBase, body) {
  const pc = (body && body.page_code && String(body.page_code).trim().toUpperCase()) || '';
  const zooTaxi = Boolean(
    body && (body.zoo_taxi === true || body.zoo_taxi === 1 || body.site === 'zoo_taxi' || body.site === 'zoo-taxi'),
  );
  if (!pc && !zooTaxi) {
    return Promise.resolve({ success: false, error: 'page_code required' });
  }
  const dedupKey = pc || 'zoo-taxi:landing';
  let p = SESSION_START_INFLIGHT.get(dedupKey);
  if (!p) {
    p = fetch(`${apiBase}/api/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withMailerFlag(body)),
    })
      .then(async (r) => {
        try {
          return await r.json();
        } catch {
          return { success: false, error: 'Invalid response' };
        }
      })
      .finally(() => {
        SESSION_START_INFLIGHT.delete(dedupKey);
      });
    SESSION_START_INFLIGHT.set(dedupKey, p);
  }
  return p;
}

function transitionSentKey(sessionId, pageCode) {
  const pc = (pageCode || '').toString().trim().toUpperCase();
  return `payment_transition_sent:${sessionId}:${pc}`;
}

/**
 * Один «переход на ввод» на пару (sessionId, pageCode) в вкладке — плюс к серверному дедупу.
 */
export function postPaymentTransitionOnce(apiBase, { log_id, session_id, page_code }) {
  const sid = (session_id || '').toString().trim();
  const pc = resolvePaymentPageCode(page_code);
  if (!log_id || !sid || !pc) return;
  const key = transitionSentKey(sid, pc);
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(key) === '1') return;
  if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, '1');
  fetch(`${apiBase}/api/log/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ log_id, session_id: sid, page_code: pc }),
  }).catch(() => {});
}
